(function () {
  const mmImportantVideoInstances = new WeakMap();

  class MMImportantVideo {
    constructor(section) {
      this.section = section;
      this.swiperElement = section.querySelector("[data-mm-important-video-swiper]");
      this.slideCount = Number(section.dataset.mmImportantVideoCount || 0);
      this.speed = Number(section.dataset.mmImportantVideoSpeed || 400);
      this.loop = section.dataset.mmImportantVideoLoop === "true";
      this.swiper = null;
      this.onClick = this.handleClick.bind(this);
      this.onSlideChange = this.handleSlideChange.bind(this);
      this.onVideoPlay = this.handleVideoPlay.bind(this);
      this.onVideoPause = this.handleVideoPause.bind(this);
      this.onVideoEnded = this.handleVideoEnded.bind(this);
      this.videoListeners = [];

      this.init();
    }

    init() {
      this.section.addEventListener("click", this.onClick);
      this.bindHostedVideoEvents();
      this.initSwiper();
      this.updateNavigationState();
    }

    destroy() {
      this.section.removeEventListener("click", this.onClick);
      this.videoListeners.forEach(({ video, type, handler }) => video.removeEventListener(type, handler));
      this.videoListeners = [];
      this.resetYouTubeSlides();
      this.resetVimeoSlides();

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    bindHostedVideoEvents() {
      this.section.querySelectorAll("[data-mm-important-video-hosted]").forEach((video) => {
        const events = [
          ["play", this.onVideoPlay],
          ["pause", this.onVideoPause],
          ["ended", this.onVideoEnded],
        ];

        events.forEach(([type, handler]) => {
          video.addEventListener(type, handler);
          this.videoListeners.push({ video, type, handler });
        });
      });
    }

    initSwiper() {
      if (this.slideCount <= 1 || !this.swiperElement || typeof window.Swiper !== "function") return;

      if (this.swiperElement.swiper) {
        this.swiper = this.swiperElement.swiper;
        return;
      }

      const previous = this.section.querySelector("[data-mm-important-video-prev]");
      const next = this.section.querySelector("[data-mm-important-video-next]");

      this.swiper = new window.Swiper(this.swiperElement, {
        slidesPerView: 1,
        speed: this.speed,
        loop: this.loop,
        navigation: {
          prevEl: previous,
          nextEl: next,
        },
        on: {
          slideChangeTransitionStart: this.onSlideChange,
        },
      });
    }

    handleClick(event) {
      const watchButton = event.target.closest("[data-mm-important-video-watch]");
      const playToggle = event.target.closest("[data-mm-important-video-play-toggle]");
      const soundToggle = event.target.closest("[data-mm-important-video-sound-toggle]");

      if (watchButton && this.section.contains(watchButton)) {
        this.startSlide(watchButton.closest("[data-mm-important-video-slide]"));
      }

      if (playToggle && this.section.contains(playToggle)) {
        this.toggleHostedPlayback(playToggle.closest("[data-mm-important-video-slide]"));
      }

      if (soundToggle && this.section.contains(soundToggle)) {
        this.toggleHostedSound(soundToggle.closest("[data-mm-important-video-slide]"));
      }
    }

    startSlide(slide) {
      if (!slide) return;

      this.pauseAllOtherSlides(slide);

      const type = slide.dataset.mmVideoType;

      if (type === "hosted") {
        this.startHostedVideo(slide);
      }

      if (type === "youtube") {
        this.startYouTubeVideo(slide);
      }

      if (type === "vimeo") {
        this.startVimeoVideo(slide);
      }
    }

    startHostedVideo(slide) {
      const video = slide.querySelector("[data-mm-important-video-hosted]");
      if (!video) return;

      slide.classList.add("is-mm-video-started");
      slide.dataset.mmVideoStarted = "true";

      const controls = slide.querySelector("[data-mm-important-video-hosted-controls]");
      if (controls) controls.hidden = false;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          this.updateHostedPlayState(slide, true);
        });
      }
    }

    startYouTubeVideo(slide) {
      const container = slide.querySelector("[data-mm-important-video-youtube]");
      if (!container) return;

      const youtubeId = this.getYouTubeId(slide.dataset.mmYoutubeUrl || "");
      if (!youtubeId) return;

      slide.classList.add("is-mm-video-started");
      slide.dataset.mmVideoStarted = "true";
      container.setAttribute("aria-hidden", "false");

      if (!container.querySelector("iframe")) {
        const iframe = document.createElement("iframe");
        iframe.className = "mm-important-video__youtube-iframe";
        iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&playsinline=1&rel=0`;
        iframe.title = "YouTube video player";
        iframe.allow = "autoplay; encrypted-media; picture-in-picture";
        iframe.allowFullscreen = true;
        container.appendChild(iframe);
      }
    }

    startVimeoVideo(slide) {
      const container = slide.querySelector("[data-mm-important-video-vimeo]");
      if (!container) return;

      const vimeo = this.getVimeoData(slide.dataset.mmVimeoUrl || "");
      if (!vimeo) return;

      slide.classList.add("is-mm-video-started");
      slide.dataset.mmVideoStarted = "true";
      container.setAttribute("aria-hidden", "false");

      if (!container.querySelector("iframe")) {
        const iframe = document.createElement("iframe");
        iframe.className = "mm-important-video__youtube-iframe mm-important-video__vimeo-iframe";
        const hashParam = vimeo.hash ? `&h=${vimeo.hash}` : "";
        iframe.src = `https://player.vimeo.com/video/${vimeo.id}?autoplay=1&playsinline=1${hashParam}`;
        iframe.title = "Vimeo video player";
        iframe.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
        iframe.allowFullscreen = true;
        container.appendChild(iframe);
      }
    }

    toggleHostedPlayback(slide) {
      const video = slide?.querySelector("[data-mm-important-video-hosted]");
      if (!video) return;

      if (video.paused) {
        this.pauseAllOtherSlides(slide);
        video.play();
      } else {
        video.pause();
      }
    }

    toggleHostedSound(slide) {
      const video = slide?.querySelector("[data-mm-important-video-hosted]");
      if (!video) return;

      video.muted = !video.muted;
      this.updateHostedSoundState(slide);
    }

    handleVideoPlay(event) {
      const slide = event.target.closest("[data-mm-important-video-slide]");
      this.pauseAllOtherSlides(slide);
      this.updateHostedPlayState(slide, false);
      this.updateHostedSoundState(slide);
    }

    handleVideoPause(event) {
      const slide = event.target.closest("[data-mm-important-video-slide]");
      this.updateHostedPlayState(slide, true);
    }

    handleVideoEnded(event) {
      const slide = event.target.closest("[data-mm-important-video-slide]");
      this.updateHostedPlayState(slide, true);
    }

    handleSlideChange() {
      const activeSlide = this.getActiveSlide();

      this.section.querySelectorAll("[data-mm-important-video-slide]").forEach((slide) => {
        if (slide === activeSlide) return;
        this.pauseHostedSlide(slide);
        this.resetYouTubeSlide(slide);
        this.resetVimeoSlide(slide);
      });

      this.updateNavigationState();
    }

    getActiveSlide() {
      if (!this.swiper) return this.section.querySelector("[data-mm-important-video-slide]");
      return this.swiper.slides[this.swiper.activeIndex] || null;
    }

    pauseAllOtherSlides(currentSlide) {
      document.querySelectorAll("[data-mm-important-video-slide]").forEach((slide) => {
        if (slide === currentSlide) return;

        const section = slide.closest("[data-mm-important-video-section]");
        const instance = section ? mmImportantVideoInstances.get(section) : null;

        if (instance) {
          instance.pauseHostedSlide(slide);
          instance.resetYouTubeSlide(slide);
          instance.resetVimeoSlide(slide);
        }
      });
    }

    pauseHostedSlide(slide) {
      const video = slide.querySelector("[data-mm-important-video-hosted]");
      if (!video) return;

      if (!video.paused) video.pause();
      this.updateHostedPlayState(slide, true);
    }

    resetYouTubeSlides() {
      this.section.querySelectorAll('[data-mm-video-type="youtube"]').forEach((slide) => this.resetYouTubeSlide(slide));
    }

    resetYouTubeSlide(slide) {
      if (slide.dataset.mmVideoType !== "youtube") return;

      const container = slide.querySelector("[data-mm-important-video-youtube]");
      if (container) {
        container.replaceChildren();
        container.setAttribute("aria-hidden", "true");
      }

      slide.classList.remove("is-mm-video-started");
      slide.dataset.mmVideoStarted = "false";
    }

    resetVimeoSlides() {
      this.section.querySelectorAll('[data-mm-video-type="vimeo"]').forEach((slide) => this.resetVimeoSlide(slide));
    }

    resetVimeoSlide(slide) {
      if (slide.dataset.mmVideoType !== "vimeo") return;

      const container = slide.querySelector("[data-mm-important-video-vimeo]");
      if (container) {
        container.replaceChildren();
        container.setAttribute("aria-hidden", "true");
      }

      slide.classList.remove("is-mm-video-started");
      slide.dataset.mmVideoStarted = "false";
    }

    updateHostedPlayState(slide, isPaused) {
      if (!slide) return;

      const button = slide.querySelector("[data-mm-important-video-play-toggle]");
      const playIcon = slide.querySelector("[data-mm-important-video-play-icon]");
      const pauseIcon = slide.querySelector("[data-mm-important-video-pause-icon]");

      if (button) button.setAttribute("aria-label", isPaused ? "Play video" : "Pause video");
      if (playIcon) playIcon.hidden = !isPaused;
      if (pauseIcon) pauseIcon.hidden = isPaused;
    }

    updateHostedSoundState(slide) {
      if (!slide) return;

      const video = slide.querySelector("[data-mm-important-video-hosted]");
      const button = slide.querySelector("[data-mm-important-video-sound-toggle]");
      if (!video || !button) return;

      const isSoundOn = !video.muted && video.volume > 0;
      button.classList.toggle("is-sound-on", isSoundOn);
      button.setAttribute("aria-label", isSoundOn ? "Turn sound off" : "Turn sound on");
      button.setAttribute("aria-pressed", String(isSoundOn));
    }

    updateNavigationState() {
      if (!this.swiper || this.loop) return;

      const previous = this.section.querySelector("[data-mm-important-video-prev]");
      const next = this.section.querySelector("[data-mm-important-video-next]");

      if (previous) previous.disabled = this.swiper.isBeginning;
      if (next) next.disabled = this.swiper.isEnd;
    }

    getYouTubeId(url) {
      if (!url) return "";

      try {
        const parsedUrl = new URL(url);

        if (parsedUrl.hostname.includes("youtu.be")) {
          return parsedUrl.pathname.replace("/", "").split("/")[0];
        }

        if (parsedUrl.searchParams.get("v")) {
          return parsedUrl.searchParams.get("v");
        }

        const embedMatch = parsedUrl.pathname.match(/\/embed\/([^/?]+)/);
        if (embedMatch) return embedMatch[1];

        const shortsMatch = parsedUrl.pathname.match(/\/shorts\/([^/?]+)/);
        if (shortsMatch) return shortsMatch[1];
      } catch (error) {
        return "";
      }

      return "";
    }

    getVimeoData(url) {
      if (!url) return null;

      try {
        const parsedUrl = new URL(url);
        const segments = parsedUrl.pathname.split("/").filter(Boolean);
        const idIndex = segments.findIndex((segment) => /^\d+$/.test(segment));
        if (idIndex === -1) return null;

        const id = segments[idIndex];
        let hash = parsedUrl.searchParams.get("h") || "";

        const nextSegment = segments[idIndex + 1];
        if (!hash && nextSegment && /^[a-z0-9]+$/i.test(nextSegment)) {
          hash = nextSegment;
        }

        return { id, hash };
      } catch (error) {
        return null;
      }
    }
  }

  function initMMImportantVideo(container = document) {
    container.querySelectorAll("[data-mm-important-video-section]").forEach((section) => {
      if (mmImportantVideoInstances.has(section)) return;
      mmImportantVideoInstances.set(section, new MMImportantVideo(section));
    });
  }

  function unloadMMImportantVideo(section) {
    const instance = mmImportantVideoInstances.get(section);
    if (!instance) return;

    instance.destroy();
    mmImportantVideoInstances.delete(section);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initMMImportantVideo());
  } else {
    initMMImportantVideo();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initMMImportantVideo(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-mm-important-video-section]").forEach((section) => {
      unloadMMImportantVideo(section);
    });
  });
})();